import { Component, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { finalize } from 'rxjs';
import { BackendService } from '@core/services';
import { ButtonComponent } from '@shared/components/button/button.component';
import {
  InputComponent,
  SelectComponent,
} from '@shared/components/form-fields';
import { ContactFormFirstVariantConfig } from '@shared/models';
import { SafePipe } from '@shared/pipes';

import { ContactFormBase } from '../contact-form-base.class';

@Component({
  selector: 'b2c-variant1',
  styleUrl: './variant1.component.scss',
  templateUrl: './variant1.component.html',
  imports: [
    SafePipe,
    FormsModule,
    InputComponent,
    ButtonComponent,
    SelectComponent,
    ReactiveFormsModule,
  ],
})
export class Variant1Component extends ContactFormBase implements OnInit {
  config = input.required<ContactFormFirstVariantConfig>();

  public textAreaMessages: Record<string, string> = {
    default: 'Exemple : Je n’arrive pas à cumuler mes parcours  gagnants.',
    participation: `Bonjour, <br/>Je suis porteur(euse) de la Carte E.Leclerc <span style="color: red;">"renseigner votre numéro de carte ici"</span> et j'ai bien donné mon accord pour bénéficier d'avantages et d'offres personnalisés de la part de l'enseigne E.Leclerc dans un minimum de 3 semaines avant l'opération Parcours Gagnants à laquelle je souhaite participer. Néanmoins je ne peux pas participer aux Parcours Gagnants. Pouvez-vous vérifier? <br/><br/>Cordialement,`,
    parcours: `Bonjour, <br/>Le <span style="color: red;">"renseigner votre date ici"</span>, j'ai effectué un/des achat(s) pour un montant de <span>"renseigner votre montant ici"</span> au magasin de <span style="color: red;">"renseigner votre nom de magasin ici"</span>. <br/>Plusieurs produits n'ont pas été pris en compte dans l'avancement de mes Parcours : \n<span style="color: red;">"renseigner votre liste des produits ici"</span> <br/><br/>Cordialement,`,
    tickets: `Bonjour, <br/>J'ai participé à l'opération Parcours Gagnants allant du <span style="color: red;">"renseigner votre dates ici"</span>. Nous sommes déjà à plus de 15 jours après la fin de l'opération et à ce jour je n'ai pas été crédité du montant correspondant sur ma Carte E.Leclerc. <br/>Pouvez-vous m'indiquer quand est-ce que le montant sera crédité? <br/><br/>Cordialement,`,
    autre: '',
  };

  private readonly backendService: BackendService = inject(BackendService);

  ngOnInit(): void {
    this._contactForm = this.createForm(this.config().contactFormBlock.fields);
    this._contactForm.addControl(
      'content',
      new FormControl('', [Validators.required])
    );
  }

  submit(): void {
    if (this.submitted()) {
      return;
    }

    if (this.form.invalid) {
      this.errorMessage.set('Veuillez compléter tous les champs ci-dessus.');
      return;
    }

    this.submitted.set(true);
    this.resetMessages();

    const { email, content, type } = this.form.value;

    const typeValue =
      type === 'parcours-bonus-plus'
        ? '[Parcours Bonus+] - '
        : '[Parcours Gagnants] - ';

    this.backendService
      .sendClaim(
        `${typeValue} ${content}`,
        this.backendService.untienotsId,
        '',
        email
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => setTimeout(() => this.resetMessages(), 2000))
      )
      .subscribe({
        next: () => {
          this.successMessage.set('Le message a bien été envoyé.');
          this.form.reset();
          this.form.markAsPristine();
          this.form.markAsUntouched();
          this.form.updateValueAndValidity();
        },
        error: () => {
          this.errorMessage.set(
            "Le message n'a pas pu être envoyé. Veuillez réessayer."
          );
        },
      });
  }

  onValueChanged(value: string, formControlName: string): void {
    if (formControlName === 'issue_types') {
      this.selectedOption = value;
      const textAreaValue = this.textAreaMessages[value];
      this.form.patchValue({
        content: textAreaValue,
      });
    }
  }

  onContentChanged(event: unknown): void {
    const tmpEvent = event as { target: { innerHTML: string } };
    const editedContent = tmpEvent.target.innerHTML;
    this.form.patchValue({
      content: editedContent,
    });
  }
}
